import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Badge } from '../components/StatusBadge';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { Editor } from '@monaco-editor/react';
import JSZip from 'jszip';
import {
  DocumentDuplicateIcon,
  MinusIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { ConverterJob, ConverterJobResult } from '../types';
import InlineFeedback from '../components/InlineFeedback';
import ConfirmDialog from '../components/ConfirmDialog';
import { useFeedback } from '../hooks/useFeedback';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';
import CopyIconButton from '../components/CopyIconButton';
import { formatDateTime, formatDuration, formatTime, getRelativeTime } from '../utils/dateTime';
import { useJobLogPolling } from '../hooks/useJobLogPolling';
import { urlEnum, urlPositiveInt, useUrlState } from '../hooks/useUrlState';
import { fromHere, useBackTo } from '../hooks/useBackTo';
import { formatCentsAsDollars } from '../utils/money';

const EDITOR_MIN_VH = 0.4;
// Breathing room below the editors: result-card bottom padding + page margin.
const EDITOR_BOTTOM_GAP_PX = 56;

const fetchLogPage = (jobId: string, afterId?: number) =>
  apiService.getConverterJobLogs(jobId, afterId);
const fetchAllLogs = (jobId: string) => apiService.getAllConverterJobLogs(jobId);

const URL_SPEC = {
  tab: urlEnum(['', 'result', 'info', 'logs'] as const, ''),
  file: urlPositiveInt(),
};

const ConverterJobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = useBackTo('/converter');
  // Cost is for saved instances only.
  const showCost = apiService.getInstanceInfo()?.instanceMode === 'persistent';
  const queryClient = useQueryClient();
  const [state, set] = useUrlState(URL_SPEC);
  const [, forceUpdate] = useState({});
  const [fontSize, setFontSize] = useState<number>(14);
  const [sourceHovered, setSourceHovered] = useState(false);
  const [convertedHovered, setConvertedHovered] = useState(false);
  const [sourceContentH, setSourceContentH] = useState<number>(0);
  const [convertedContentH, setConvertedContentH] = useState<number>(0);
  const [viewportH, setViewportH] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 800,
  );

  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Measured document-top offset of the editor row. Lets the editors grow to
  // exactly the space below the page chrome instead of guessing its height.
  const editorAreaRef = useRef<HTMLDivElement | null>(null);
  const [editorTop, setEditorTop] = useState<number | null>(null);
  // No dependency array on purpose: any render can move the editor row
  // (tabs, error banner, file strip); the jitter guard below breaks cycles.
  // Measuring and resizing are deferred to the next frame so the resize never
  // lands inside a ResizeObserver callback (Monaco observes the container).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      const el = editorAreaRef.current;
      if (!el) {
        setEditorTop(null);
        return;
      }
      const next = Math.round(el.getBoundingClientRect().top + window.scrollY);
      // Ignore sub-2px jitter so re-measuring on every render can't loop.
      setEditorTop((prev) => (prev != null && Math.abs(prev - next) < 2 ? prev : next));
    });
    return () => cancelAnimationFrame(raf);
  });

  const editorHeight = (() => {
    const minPx = Math.round(viewportH * EDITOR_MIN_VH);
    const available =
      editorTop != null ? viewportH - editorTop - EDITOR_BOTTOM_GAP_PX : minPx;
    const maxPx = Math.max(minPx, available);
    const content = Math.max(sourceContentH, convertedContentH);
    return Math.min(maxPx, Math.max(minPx, content));
  })();

  const increaseFontSize = () => setFontSize((prev) => Math.min(prev + 1, 24));
  const decreaseFontSize = () => setFontSize((prev) => Math.max(prev - 1, 10));

  const deleteFeedback = useFeedback();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const downloadFeedback = useFeedback();
  const logsCopy = useCopyToClipboard();
  const sourceCopy = useCopyToClipboard();
  const convertedCopy = useCopyToClipboard();

  const { data: job, isLoading, error: jobError } = useQuery({
    queryKey: ['converter-job', id],
    queryFn: () => apiService.getConverterJob(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data as ConverterJob | undefined;
      return data?.status === 'running' || data?.status === 'pending' ? 2000 : false;
    },
  });

  const { logs, failed: logsFailed } = useJobLogPolling(id, job?.status, fetchLogPage, fetchAllLogs);

  const isCompleted = job?.status === 'completed';
  const showResultTab = isCompleted;
  const activeTab = state.tab === 'result' && !showResultTab
    ? 'info'
    : state.tab || (isCompleted ? 'result' : 'info');

  const {
    data: result,
    isError: resultError,
    error: resultQueryError,
    isLoading: resultLoading,
    isFetching: resultFetching,
    refetch: refetchResult,
  } = useQuery<ConverterJobResult, AxiosError<{ message?: string }>>({
    queryKey: ['converter-job-result', id],
    queryFn: () => apiService.getConverterJobResult(id!),
    enabled: !!id && isCompleted,
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteConverterJob(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['converter-jobs'] });
      navigate(backHref);
    },
    onError: (error: any) => {
      setShowDeleteConfirm(false);
      deleteFeedback.showError(error.response?.data?.message || 'Failed to delete job.');
    },
  });

  // Tick elapsed time while running.
  useEffect(() => {
    if (job?.status === 'running') {
      const interval = setInterval(() => forceUpdate({}), 1000);
      return () => clearInterval(interval);
    }
  }, [job?.status]);

  const copyLogsToClipboard = () => {
    const logText = logs
      .map((log) => `[${formatTime(log.timestamp)}] ${log.level}: ${log.message}`)
      .join('\n');
    logsCopy.copy(logText);
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadFile = (text: string, filename: string) => {
    triggerDownload(new Blob([text], { type: 'text/plain;charset=utf-8' }), filename);
  };

  const downloadBasename = (filename?: string) => {
    const parts = (filename || '').split(/[\\/]/).filter(Boolean);
    return parts[parts.length - 1] || 'converted.py';
  };

  const downloadOutputFiles = async () => {
    if (outputFiles.length === 0) return;
    downloadFeedback.clear();
    if (outputFiles.length === 1) {
      const file = outputFiles[0];
      downloadFile(file.code || '', downloadBasename(file.filename));
      return;
    }
    try {
      const zip = new JSZip();
      outputFiles.forEach((file, index) => {
        const name = file.filename || `converted_${index + 1}.py`;
        zip.file(name, file.code || '');
      });
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const base = (job?.project_name || resultFilename || 'converted').replace(/\.[^.]+$/, '');
      const safe = base.replace(/[^a-zA-Z0-9-_]/g, '_') || 'converted';
      triggerDownload(zipBlob, `${safe}.zip`);
    } catch (err) {
      downloadFeedback.showError('Failed to create zip file.');
    }
  };


  if (isLoading) {
    return <div className="text-center py-8 text-secondary">Loading...</div>;
  }

  // Only when nothing loaded: a failed poll must not wipe a job already on screen.
  if (jobError && !job) {
    const notFound = (jobError as any)?.response?.status === 404;
    return (
      <div className={`text-center py-8 ${notFound ? 'text-secondary' : 'text-red-400'}`}>
        {notFound ? 'Job not found' : 'Failed to load the job.'}
      </div>
    );
  }

  if (!job) {
    return <div className="text-center py-8 text-secondary">Job not found</div>;
  }

  const inputType = job.parameters?.input_type;
  const sourceFilename = job.parameters?.source_filename;
  const resultFilename = job.parameters?.result_filename;
  const targetType = job.parameters?.target_type;
  const targetLabel = targetType === 'dbr' ? 'Databricks Notebook' : 'PySpark';
  const outputLabel = targetType === 'dbr' ? 'Databricks Notebook (Output)' : 'PySpark Code (Output)';
  const sourceCodeAvailable =
    result?.source_code_available ?? Boolean(result?.source_code);
  const hasReadableSource = Boolean(sourceCodeAvailable && result?.source_code);
  const outputFiles = result?.files || [];
  const activeOutputIndex = Math.min(state.file - 1, Math.max(0, outputFiles.length - 1));
  const activeOutputFile =
    outputFiles.length > 0
      ? outputFiles[Math.min(activeOutputIndex, outputFiles.length - 1)]
      : null;

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-end gap-3">
          <h2 className="text-2xl font-bold">
            {job.project_name || sourceFilename || `Job ${job.id}`}
          </h2>
          <Badge value={job.status} size="sm" />
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() => {
              deleteFeedback.clear();
              setShowDeleteConfirm(true);
            }}
            disabled={deleteMutation.isPending}
            className="btn-danger"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
          <InlineFeedback feedback={deleteFeedback.feedback} />
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={job.status === 'running' || job.status === 'pending' ? 'Cancel Conversion Job' : 'Delete Conversion Job'}
        message={
          job.status === 'running' || job.status === 'pending'
            ? 'This stops the job. It stays in the list as cancelled. Delete it again to remove it.'
            : 'This deletes the job, its source files and its converted output. You cannot undo it.'
        }
        confirmLabel={job.status === 'running' || job.status === 'pending' ? 'Stop Job' : 'Delete Job'}
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Progress bar while running */}
      {job.status === 'running' && (
        <div className="bg-widget-bg rounded-lg border border-secondary p-6 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-secondary">Progress</span>
            <span className="text-heading">{job.progress}%</span>
          </div>
          <div className="w-full bg-body-bg rounded-full h-3">
            <div
              className="bg-primary h-3 rounded-full transition-all duration-300"
              style={{ width: `${job.progress}%` }}
            />
          </div>
          {job.current_step && (
            <p className="text-sm text-secondary mt-4">
              Current step: <span className="text-heading">{job.current_step}</span>
            </p>
          )}
        </div>
      )}

      {/* Error banner */}
      {job.error_message && (
        <div className="bg-red-500/25 border border-red-500 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-2 text-red-500">Error</h3>
          <p className="text-red-400 font-mono text-sm whitespace-pre-wrap">
            {job.error_message}
          </p>
        </div>
      )}

      {/* Tabs Header */}
      <div className="flex border-b border-secondary">
        {showResultTab && (
          <button
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'result'
                ? 'text-heading border-b-2 border-primary'
                : 'text-secondary hover:text-heading'
            }`}
            onClick={() => set({ tab: 'result' })}
          >
            Result
          </button>
        )}
        <button
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'info'
              ? 'text-heading border-b-2 border-primary'
              : 'text-secondary hover:text-heading'
          }`}
          onClick={() => set({ tab: 'info' })}
        >
          Job Information
        </button>
        <button
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'logs'
              ? 'text-heading border-b-2 border-primary'
              : 'text-secondary hover:text-heading'
          }`}
          onClick={() => set({ tab: 'logs' })}
        >
          Logs
        </button>
      </div>

      <div className="mt-6">
        {activeTab === 'result' && showResultTab && (
          <div className="space-y-6">
            {result ? (
              <div
                className={`bg-widget-bg rounded-lg border border-secondary p-6 grid grid-cols-1 gap-6 ${
                  hasReadableSource ? 'lg:grid-cols-2' : ''
                }`}
              >
                {hasReadableSource && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 h-6">
                      <h3 className="text-sm font-medium text-heading">
                        SAS Code (Input)
                        {result.source_filename && (
                          <span className="text-secondary font-normal ml-2">
                            {result.source_filename}
                          </span>
                        )}
                      </h3>
                    </div>
                    <div
                      className="overflow-visible relative"
                      style={{ height: `${editorHeight}px` }}
                      onMouseEnter={() => setSourceHovered(true)}
                      onMouseLeave={() => setSourceHovered(false)}
                    >
                      {sourceHovered && (
                        <div className="absolute top-2 right-6 z-10 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={decreaseFontSize}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title="Decrease font size"
                          >
                            <MinusIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={increaseFontSize}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title="Increase font size"
                          >
                            <PlusIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => sourceCopy.copy(result.source_code || '')}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title={sourceCopy.status === 'copied' ? 'Copied!' : sourceCopy.status === 'error' ? 'Failed to copy source code' : 'Copy source code'}
                          >
                            {sourceCopy.status === 'copied' ? (
                              <CheckIcon className="w-4 h-4 text-primary" />
                            ) : sourceCopy.status === 'error' ? (
                              <XMarkIcon className="w-4 h-4 text-red-400" />
                            ) : (
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}
                      <Editor
                        height="100%"
                        defaultLanguage="sas"
                        value={result.source_code || ''}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          wordWrap: 'on',
                          automaticLayout: true,
                          padding: { top: 10, bottom: 10 },
                          contextmenu: false,
                        }}
                        onMount={(editor) => {
                          // Defer to the next frame: resizing the container
                          // inside Monaco's ResizeObserver callback triggers
                          // "ResizeObserver loop completed" errors.
                          const update = () => {
                            const h = Math.ceil(editor.getContentHeight());
                            requestAnimationFrame(() => setSourceContentH(h));
                          };
                          editor.onDidContentSizeChange(update);
                          update();
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-2 h-6">
                    <h3 className="text-sm font-medium text-heading">
                      {outputLabel}
                      {outputFiles.length <= 1 && activeOutputFile?.filename && (
                        <span className="text-secondary font-normal ml-2">
                          {activeOutputFile.filename}
                        </span>
                      )}
                    </h3>
                    <button
                      type="button"
                      onClick={downloadOutputFiles}
                      disabled={outputFiles.length === 0}
                      className="p-1 text-secondary hover:text-heading transition-colors"
                      title={outputFiles.length > 1 ? 'Download all files as zip' : 'Download file'}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <InlineFeedback feedback={downloadFeedback.feedback} className="mb-2" />
                  <div>
                    {outputFiles.length > 1 && (
                      <div className="flex overflow-x-auto">
                        {outputFiles.map((file, index) => {
                          const isActive = activeOutputIndex === index;
                          return (
                            <button
                              key={`${file.filename}-${index}`}
                              type="button"
                              onClick={() => set({ file: index + 1 })}
                              title={file.filename}
                              className={`px-4 py-1.5 text-xs font-mono whitespace-nowrap truncate max-w-[400px] border-t border-r first:border-l transition-colors ${
                                isActive
                                  ? 'bg-[#1e1e1e] text-heading border-secondary border-t-primary'
                                  : 'bg-widget-bg text-secondary hover:text-heading border-secondary'
                              }`}
                            >
                              {file.filename}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  <div
                    ref={editorAreaRef}
                    className="overflow-visible relative"
                    style={{ height: `${editorHeight}px` }}
                    onMouseEnter={() => setConvertedHovered(true)}
                    onMouseLeave={() => setConvertedHovered(false)}
                  >
                    {convertedHovered && (
                      <div className="absolute top-2 right-6 z-10 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={decreaseFontSize}
                          className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                          title="Decrease font size"
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={increaseFontSize}
                          className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                          title="Increase font size"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => convertedCopy.copy(activeOutputFile?.code || '')}
                          className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                          title={convertedCopy.status === 'copied' ? 'Copied!' : convertedCopy.status === 'error' ? 'Failed to copy converted code' : 'Copy converted code'}
                        >
                          {convertedCopy.status === 'copied' ? (
                            <CheckIcon className="w-4 h-4 text-primary" />
                          ) : convertedCopy.status === 'error' ? (
                            <XMarkIcon className="w-4 h-4 text-red-400" />
                          ) : (
                            <DocumentDuplicateIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                    <Editor
                      height="100%"
                      defaultLanguage="python"
                      value={activeOutputFile?.code || ''}
                      theme="vs-dark"
                      onMount={(editor) => {
                        // Deferred for the same ResizeObserver reason as the
                        // source editor above.
                        const update = () => {
                          const h = Math.ceil(editor.getContentHeight());
                          requestAnimationFrame(() => setConvertedContentH(h));
                        };
                        editor.onDidContentSizeChange(update);
                        update();
                      }}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        automaticLayout: true,
                        padding: { top: 10, bottom: 10 },
                        contextmenu: false,
                      }}
                    />
                  </div>
                  </div>
                </div>
              </div>
            ) : resultLoading ? (
              <p className="text-secondary text-center py-8">Loading result...</p>
            ) : resultError ? (
              <div className="text-center py-8 space-y-4">
                <p className="text-secondary">
                  {resultQueryError?.response?.data?.message || 'Failed to load the result.'}
                </p>
                {resultQueryError?.response?.status !== 404 && (
                  <button
                    type="button"
                    onClick={() => refetchResult()}
                    disabled={resultFetching}
                    className="btn-secondary"
                  >
                    {resultFetching ? 'Retrying...' : 'Retry'}
                  </button>
                )}
              </div>
            ) : (
              <p className="text-secondary text-center py-8">No result available.</p>
            )}
          </div>
        )}

        {activeTab === 'info' && (
          <div className="bg-widget-bg rounded-lg border border-secondary p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left column: identity + config; right column: timeline. */}
              <div>
                <dt className="text-secondary text-sm">Job ID</dt>
                <dd className="text-heading font-mono flex items-center gap-2">
                  {job.id}
                  <CopyIconButton text={job.id} label="Job ID" />
                </dd>
              </div>
              <div>
                <dt className="text-secondary text-sm">Created</dt>
                <dd className="text-heading">
                  {formatDateTime(job.created_at)}
                  {job.created_at && (
                    <span className="text-secondary ml-1">
                      ({getRelativeTime(job.created_at)})
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-secondary text-sm">Target</dt>
                <dd className="text-heading">{targetLabel}</dd>
              </div>
              {job.started_at && (
                <div>
                  <dt className="text-secondary text-sm">Started</dt>
                  <dd className="text-heading">
                    {formatDateTime(job.started_at)}
                    <span className="text-secondary ml-1">
                      ({getRelativeTime(job.started_at)})
                    </span>
                  </dd>
                </div>
              )}
              {inputType && (
                <div>
                  <dt className="text-secondary text-sm">Input type</dt>
                  <dd className="text-heading">{inputType}</dd>
                </div>
              )}
              {job.completed_at && (
                <div>
                  <dt className="text-secondary text-sm">Completed</dt>
                  <dd className="text-heading">
                    {formatDateTime(job.completed_at)}
                    <span className="text-secondary ml-1">
                      ({getRelativeTime(job.completed_at)})
                    </span>
                  </dd>
                </div>
              )}
              {sourceFilename && (
                <div>
                  <dt className="text-secondary text-sm">Source filename</dt>
                  <dd className="text-heading font-mono">{sourceFilename}</dd>
                </div>
              )}
              <div>
                <dt className="text-secondary text-sm">Time Elapsed</dt>
                <dd className="text-heading">
                  {formatDuration(job.started_at, job.completed_at)}
                </dd>
              </div>
              {job.input_tokens_converted != null && (
                <>
                  <div>
                    <dt className="text-secondary text-sm">Converted Input Tokens</dt>
                    <dd className="text-heading">{job.input_tokens_converted.toLocaleString()}</dd>
                  </div>
                  {/* Cost and usage are for saved instances only. */}
                  {showCost && (
                    <div>
                      <dt className="text-secondary text-sm">Cost</dt>
                      <dd className="text-heading flex items-center gap-3">
                        {formatCentsAsDollars(job.cost_cents)}
                        <Link
                          to={`/usage/web/jobs/${job.id}`}
                          state={fromHere(location)}
                          className="text-primary hover:underline"
                        >
                          Open usage
                        </Link>
                      </dd>
                    </div>
                  )}
                </>
              )}
            </dl>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="bg-widget-bg rounded-lg border border-secondary relative">
            {logs.length > 0 ? (
              <>
                <button
                  onClick={copyLogsToClipboard}
                  className="absolute top-2 right-2 z-10 p-2 rounded-md text-secondary hover:text-primary hover:bg-primary/10 transition-colors"
                  title={logsCopy.status === 'copied' ? 'Copied!' : logsCopy.status === 'error' ? 'Failed to copy logs' : 'Copy logs to clipboard'}
                >
                  {logsCopy.status === 'copied' ? (
                    <CheckIcon className="w-4 h-4 text-primary" />
                  ) : logsCopy.status === 'error' ? (
                    <XMarkIcon className="w-4 h-4 text-red-400" />
                  ) : (
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  )}
                </button>
                <div className="p-6">
                  {logsFailed && (
                    <p className="text-red-400 text-sm mb-4 pr-10">
                      Failed to load all logs. Showing what loaded.
                    </p>
                  )}
                  <div className="space-y-1 font-mono text-sm">
                    {logs.map((log, index) => (
                      <div key={log.id || index} className="flex gap-2">
                        <span className="text-secondary whitespace-nowrap">
                          {formatTime(log.timestamp)}
                        </span>
                        <span
                          className={
                            log.level === 'ERROR'
                              ? 'text-red-500'
                              : log.level === 'WARNING'
                              ? 'text-warning'
                              : 'text-body-color'
                          }
                        >
                          {log.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="p-6">
                <p className={`${logsFailed ? 'text-red-400' : 'text-secondary'} text-center py-8`}>
                  {logsFailed ? 'Failed to load logs.' : 'No logs available'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConverterJobDetail;
